import { updatePaginationState } from "@/store/paginationStore";
import { Button } from "@/components/ui/button";
import { RefreshCcwIcon } from "lucide-react";
import { PaginatedHolidaysQueryVariables, HolidayFilters } from "@/graphql/__generated__/graphql";
import { HolidaysDateRange, HolidaysProvinceDropdown, HolidaysSortingDropdown } from "./Components";

interface Props {
  onFilterSubmit: (_variables: PaginatedHolidaysQueryVariables) => void;
  displayAmount: number;
}

/**
 * HolidaysFilter component handles filtering and sorting of holidays
 * It manages filter state and provides UI for filter controls
 * @param props Component properties
 * @returns JSX element
 */
const HolidaysFilter = (props: Props) => {
  const { onFilterSubmit, displayAmount } = props;

  /**
   * Handles filter submission
   * @param newValues New filter values to submit
   */
  const handleSubmitClick = (newValues: PaginatedHolidaysQueryVariables) => {
    onFilterSubmit(newValues);
  };

  /**
   * Resets all filter values and sort settings
   * Updates both local state and triggers query update
   */
  const handleResetClickFilters = () => {
    // Define empty filters object
    const emptyFilters = {
      date: null,
      provinces: null,
    };

    // Reset all filter values and sort in the global store
    updatePaginationState({
      paginatedHolidays: {
        filters: emptyFilters as HolidayFilters,
        sort: null
      }
    });

    // Create reset query variables with explicitly empty filters and sort
    const updatedVariables: PaginatedHolidaysQueryVariables = {
      first: displayAmount,
      filters: {
        date: null,
        provinces: null,
      },
      sort: null
    };

    // Immediately submit the reset filter state to parent component
    onFilterSubmit(updatedVariables);
  };

  return (
    <div className="flex flex-1 gap-2">
      <div className="flex flex-1 items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Province Multi-Select - Controls province filter */}
          <HolidaysProvinceDropdown
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />

          {/* Date Range - Controls date filter */}
          <HolidaysDateRange
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />

          {/* Sorting Dropdown - Controls sort field and direction */}
          <HolidaysSortingDropdown
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />
        </div>
        <div>
          <Button variant="outline" size="icon" onClick={handleResetClickFilters}>
            <RefreshCcwIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HolidaysFilter;
